import { SITE_NAME } from '@constants/site';
import {
  buildReturnPolicy,
  buildShippingDetails,
  organizationRef
} from './organization';
import { onlyStrings, stripTags, toNumber, truncate } from './text';
import type { JsonLdDocument, JsonLdNode } from './types';
import { SCHEMA_CONTEXT } from './types';

export interface ProductAttributeSource {
  code?: string | null;
  title?: string | null;
  type?: string | null;
  value?: string | null;
  number?: number | null;
  bool?: boolean | null;
  unitCode?: string | null;
  unitText?: string | null;
}

export interface ProductJsonLdSource {
  id?: string | number | null;
  title?: string | null;
  subtitle?: string | null;
  text?: string | null;
  images?: {
    original?: Array<string | null> | null;
    big?: Array<string | null> | null;
    square?: Array<string | null> | null;
  } | null;
  price?: number | string | null;
  oldPrice?: number | string | null;
  available?: boolean | null;
  isPreorder?: boolean | string | null;
  isProductNotForSale?: boolean | null;
  rating_comments?: number | null;
  comments_number?: number | null;
  countryOfOrigin?: string | null;
  attributes?: ProductAttributeSource[] | null;
  is_our?: boolean | number | null;
  ingredients?: string | null;
  storage_conditions?: string | null;
  gost?: string | null;
  energy_value?: string | number | null;
  nutritional_value?: string | null;
}

const MAX_DESCRIPTION = 1000;

export type RatingCountProperty = 'reviewCount' | 'ratingCount';

export const buildAggregateRating = (
  rating: number | string | null | undefined,
  count: number | string | null | undefined,
  countProperty: RatingCountProperty = 'reviewCount'
): JsonLdNode | null => {
  const total = toNumber(count);
  const value = toNumber(rating);
  if (!total || total <= 0 || !value || value <= 0) {
    return null;
  }
  return {
    '@type': 'AggregateRating',
    ratingValue: Number(Math.min(value, 5).toFixed(1)),
    [countProperty]: Math.round(total),
    bestRating: 5,
    worstRating: 1
  };
};

const availabilityOf = (source: ProductJsonLdSource): string => {
  if (source.isPreorder) {
    return 'https://schema.org/PreOrder';
  }
  return source.available
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';
};

const propertyValue = (
  name: string,
  value: string | number | null | undefined
): JsonLdNode | null => {
  const text = stripTags(
    value === null || value === undefined ? '' : `${value}`
  );
  return text ? { '@type': 'PropertyValue', name, value: text } : null;
};

export const shownAttributes = <T extends ProductAttributeSource>(
  attributes: T[] | null | undefined
): T[] =>
  (attributes ?? []).filter(
    item => !!stripTags(item.title) && !!stripTags(item.value)
  );

const attributeValue = (
  item: ProductAttributeSource
): string | number | boolean | null => {
  if (item.type === 'number' && typeof item.number === 'number') {
    return item.number;
  }
  if (item.type === 'bool' && typeof item.bool === 'boolean') {
    return item.bool;
  }
  return stripTags(item.value) || null;
};

const attributeProperty = (item: ProductAttributeSource): JsonLdNode | null => {
  const name = stripTags(item.title);
  const value = attributeValue(item);
  if (!name || value === null) {
    return null;
  }
  const node: JsonLdNode = { '@type': 'PropertyValue', name, value };
  const code = item.code?.trim();
  if (code) {
    node.propertyID = code;
  }
  if (typeof value === 'number') {
    const unitCode = item.unitCode?.trim();
    const unitText = item.unitText?.trim();
    if (unitCode) {
      node.unitCode = unitCode;
    }
    if (unitText) {
      node.unitText = unitText;
    }
  }
  return node;
};

const strikethroughPrice = (
  oldPrice: number | null,
  price: number
): JsonLdNode | undefined =>
  oldPrice !== null && oldPrice > price
    ? {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: oldPrice,
        priceCurrency: 'RUB'
      }
    : undefined;

export const buildProduct = (
  source: ProductJsonLdSource | null | undefined,
  url: string
): JsonLdDocument | null => {
  const name = source?.title?.trim();
  if (!source || !name) {
    return null;
  }

  const description = truncate(
    [source.subtitle?.trim(), stripTags(source.text)]
      .filter(part => !!part)
      .join('. '),
    MAX_DESCRIPTION
  );
  const image = onlyStrings(
    source.images?.original?.length
      ? source.images.original
      : source.images?.big?.length
      ? source.images.big
      : source.images?.square
  );
  const price = toNumber(source.price);
  const oldPrice = toNumber(source.oldPrice);
  const isForSale = !source.isProductNotForSale;
  const country = isForSale ? source.countryOfOrigin?.trim() : undefined;
  const additionalProperty = [
    ...shownAttributes(source.attributes).map(attributeProperty),
    propertyValue('Состав', source.ingredients),
    propertyValue('Энергетическая ценность', source.energy_value),
    propertyValue('Пищевая ценность', source.nutritional_value),
    propertyValue('Условия хранения', source.storage_conditions),
    propertyValue('ГОСТ', source.gost)
  ].filter((item): item is JsonLdNode => item !== null);

  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Product',
    '@id': `${url}#product`,
    url,
    name,
    description: description || undefined,
    image: image.length ? image : undefined,
    sku:
      source.id === null || source.id === undefined
        ? undefined
        : `${source.id}`,
    brand: source.is_our ? { '@type': 'Brand', name: SITE_NAME } : undefined,
    countryOfOrigin: country
      ? { '@type': 'Country', name: country }
      : undefined,
    additionalProperty:
      isForSale && additionalProperty.length ? additionalProperty : undefined,
    aggregateRating:
      buildAggregateRating(source.rating_comments, source.comments_number) ??
      undefined,
    offers:
      price !== null && price > 0 && isForSale
        ? {
            '@type': 'Offer',
            url,
            price,
            priceCurrency: 'RUB',
            priceSpecification: strikethroughPrice(oldPrice, price),
            availability: availabilityOf(source),
            itemCondition: 'https://schema.org/NewCondition',
            seller: organizationRef(),
            hasMerchantReturnPolicy: buildReturnPolicy(),
            shippingDetails: buildShippingDetails()
          }
        : undefined
  };
};
